/**
 * DIAGNOSTIC GAME — Result Interpretations
 *
 * STRICT RULE: Keyed by base number. Each base number (1-9) gets its own
 * isolated set of result texts: a (Plus), b (Minus), c (Center), tie (Crossroads).
 *
 * NOTE: The generic one-sentence intro is NOT stored here. It is generated
 * dynamically per result type in components/diagnostic/ResultInterpretation.jsx
 * (RESULT_PREFIXES, rendered as a badge). The texts below contain ONLY the unique,
 * deep character description per base number.
 *
 * TONE RULES (empowering, game/diagnostic — NOT medical):
 *   - Type A (Plus): celebrate, inspire, reinforce the high-vibrational state.
 *     No heavy "blind spots" / "losing energy" language. Balancing tips stay
 *     extremely gentle and optional.
 *   - Type B (Minus): explicitly frame as a TEMPORARY, current state of mind.
 *     Reassure that energy can shift tomorrow and invite the user to try another
 *     date or explore other self-discovery tools on the platform.
 *
 * Grammar: 2nd person singular (Tu), neutral, simple and grounded tone.
 * paragraphs[0] starts lowercase (it continues the dynamic badge prefix).
 *
 * Structure:
 *   DIAGNOSTIC_INTERPRETATIONS[baseNumber][resultType] →
 *     { title, color, paragraphs: [..] }
 *   resultType ∈ 'a' | 'b' | 'c' | 'tie'
 *   color ∈ 'emerald' | 'amber' | 'purple' | 'blue'
 */

export const DIAGNOSTIC_INTERPRETATIONS = {
  // ═══════════════════════════════════════════════════════════
  // BASE NUMBER 1 — Leadership, will, independence
  // ═══════════════════════════════════════════════════════════
  1: {
    a: {
      title: 'Tavo energija: Aktyvi realizacija (Pliuso būsena)',
      color: 'emerald',
      paragraphs: [
        'tu žinai, kaip išnaudoti savo turimą potencialą – pats valdai savo gyvenimo vairą ir drąsiai brėži ribas.',
        'Tai stipri ir įkvepianti būsena – tu eini savo keliu, priimi sprendimus ir kuri tai, kuo tiki. Mėgaukis šiuo vidiniu tvirtumu ir leisk jam tave nešti pirmyn. O kai panorėsi šią galią pajusti dar pilniau, portale rasi kitų savęs pažinimo įrankių, kurie švelniai palydės tave dar toliau.',
      ],
    },
    b: {
      title: 'Tavo energija: Pasirinktas saugumas (Minuso būsena)',
      color: 'amber',
      paragraphs: [
        'šiuo metu tavo prigimtinis potencialas tarsi prislopęs, o atsakomybę mieliau perleidi aplinkybėms.',
        'Svarbu atsiminti – tai tik tavo dabartinė būsena, o ne nuosprendis. Galbūt šiuo metu tiesiog kaupi jėgas ramesniame kelkraštyje, ir tai visiškai natūralu. Energija nestovi vietoje: jau rytoj tavo pasirinkimai gali pakrypti kita linkme. Pabandyk įvesti kitą datą arba pasinaudok kitais portalo savęs pažinimo įrankiais – jie švelniai padės sugrįžti prie tavo prigimtinės jėgos ir vidinio lyderio.',
      ],
    },
    c: {
      title: 'Tavo energija: Situacinis lankstumas (Per vidurį)',
      color: 'purple',
      paragraphs: [
        'tu nuolat balansuoji tarp aktyvaus pliuso ir pasyvaus minuso, ieškodamas lankstaus kompromiso.',
        'Toks lankstumas puikiai saugo tave nuo perdegimo ar konfliktų ir leidžia prisitaikyti prie įvairių situacijų. Tiesiog atsimink, kad tavyje glūdi ir aiški asmeninė ašis – kuo labiau ja pasitikėsi, tuo mažiau plaukiosi pagal aplinkybes.\nKviečiame pasinaudoti kitais portalo savęs pažinimo įrankiais, kurie padės sukalibruoti aiškią tavo kurso kryptį.',
      ],
    },
    tie: {
      title: 'Tavo energija: Potencialo Kryžkelė (Maksimalus svyravimas)',
      color: 'blue',
      paragraphs: [
        'vienu metu savyje jauti ir stiprų norą lyderiauti (Pliusas), ir poreikį atsitraukti į saugumą (Minusas), ir gebėjimą prisitaikyti (Lankstumas).',
        'Tai įdomus pereinamasis taškas – tavyje vienu metu gyvena trys skirtingos kryptys. Tai natūralu ir laikina: kai sąmoningai pasirinksi savo pagrindinę ašį, jėgos nustos blaškytis ir susitelks. Ši diagnostika – švelnus kvietimas stabtelėti ir įsiklausyti į save.\nKviečiame užsiregistruoti, išsaugoti šiuos duomenis ir pasinaudoti portalo įrankiais, kurie padės sukalibruoti tavo vidinį kompasą.',
      ],
    },
  },

  // ═══════════════════════════════════════════════════════════
  // BASE NUMBER 2 — Intuition, empathy, diplomacy, cooperation
  // ═══════════════════════════════════════════════════════════
  2: {
    a: {
      title: 'Tavo energija: Sąmoninga jungtis (Pliuso būsena)',
      color: 'emerald',
      paragraphs: [
        'tu moki pasitikėti savo nuojauta ir kurti ryšį su žmonėmis neprarasdamas savęs. Jauti aplinkos nuotaikas, gebi sujungti skirtingas puses ir randi žodžius, kurie ramina.',
        'Tai šilta ir vertinga būsena – tavo jautrumas ir diplomatija dovanoja darną tau ir aplinkiniams. Džiaukis šiuo gebėjimu jungti ir suprasti. O kai panorėsi šį subtilų talentą puoselėti dar giliau, portale rasi kitų savęs pažinimo įrankių, kurie švelniai palydės tave toliau.',
      ],
    },
    b: {
      title: 'Tavo energija: Ištirpimas kituose (Minuso būsena)',
      color: 'amber',
      paragraphs: [
        'šiuo metu savo poreikius dažnai nustumi į šalį, o ramybę bandai išsaugoti tylėdamas ir prisitaikydamas.',
        'Svarbu atsiminti – tai tik tavo dabartinė būsena, o ne tavo esmė. Galbūt šiandien tiesiog daugiau rūpiniesi kitais nei savimi, ir tai suprantama. Energija nestovi vietoje: jau rytoj tavo pasirinkimai gali pakrypti kita linkme. Pabandyk įvesti kitą datą arba pasinaudok kitais portalo savęs pažinimo įrankiais – jie švelniai padės atrasti tavo ribas ir sugrįžti į savo gyvenimo centrą.',
      ],
    },
    c: {
      title: 'Tavo energija: Atsargus balansas (Per vidurį)',
      color: 'purple',
      paragraphs: [
        'tu nuolat ieškai saugaus kompromiso tarp savo ir kitų poreikių, vengdamas tiek visiško ištirpimo, tiek atviros konfrontacijos.',
        'Toks lankstumas padeda išlaikyti taiką ir santykius – tai gražus gebėjimas. Tiesiog atsimink, kad ir tavo paties tiesa nusipelno būti išsakyta: kuo aiškiau girdėsi save, tuo darnesni bus ir tavo santykiai.\nKviečiame pasinaudoti kitais portalo savęs pažinimo įrankiais, kurie padės ramiai, bet tvirtai išgirsti bei išreikšti savo poreikius.',
      ],
    },
    tie: {
      title: 'Tavo energija: Potencialo Kryžkelė (Maksimalus svyravimas)',
      color: 'blue',
      paragraphs: [
        'vienu metu savyje jauti ir gebėjimą jautriai jungti žmones (Pliusas), ir polinkį ištirpti kituose (Minusas), ir norą saugiai prisitaikyti (Lankstumas).',
        'Tai įdomus pereinamasis taškas – tavyje vienu metu gyvena kelios skirtingos kryptys. Tai natūralu ir laikina: kai aiškiau atskirsi savo poreikius nuo aplinkos lūkesčių, jausies tvirčiau ir ramiau. Ši diagnostika – švelnus kvietimas stabtelėti ir įsiklausyti į save.\nKviečiame užsiregistruoti, išsaugoti šiuos duomenis ir pasinaudoti portalo įrankiais, kurie padės sukalibruoti tavo vidinį kompasą.',
      ],
    },
  },

  // ═══════════════════════════════════════════════════════════
  // BASE NUMBER 3 — Creativity, self-expression, communication, joy
  // ═══════════════════════════════════════════════════════════
  3: {
    a: {
      title: 'Tavo energija: Kūrybinis spindesys (Pliuso būsena)',
      color: 'emerald',
      paragraphs: [
        'tu drąsiai reiški save, kuri ir dalini aplinkai savo lengvumą bei optimizmą. Moki įkvėpti, prajuokinti ir paversti net pilką dieną šviesesne.',
        'Tai spindinti, gyvybinga būsena – tavo kūrybiškumas ir džiaugsmas užkrečia visus aplinkui. Mėgaukis savo šviesa ir leisk jai laisvai trykšti. O kai panorėsi šį kūrybinį polėkį pajusti dar pilniau, portale rasi kitų savęs pažinimo įrankių, kurie švelniai palydės tave toliau.',
      ],
    },
    b: {
      title: 'Tavo energija: Užgesusi saviraiška (Minuso būsena)',
      color: 'amber',
      paragraphs: [
        'šiuo metu savo kūrybinę galią ir džiaugsmą tarsi pristabdei – dažniau abejoji savimi nei reiškiesi.',
        'Svarbu atsiminti – tai tik tavo dabartinė būsena, o ne tavo tikrasis veidas. Galbūt šiandien tiesiog stinga akimirkos pasidžiaugti savimi, ir tai praeis. Energija nestovi vietoje: jau rytoj tavo pasirinkimai gali pakrypti kita linkme. Pabandyk įvesti kitą datą arba pasinaudok kitais portalo savęs pažinimo įrankiais – jie švelniai padės vėl atrasti tavo vidinę šviesą ir saviraiškos džiaugsmą.',
      ],
    },
    c: {
      title: 'Tavo energija: Santūri saviraiška (Per vidurį)',
      color: 'purple',
      paragraphs: [
        'tu reiški save atsargiai – kuri ir kalbi tik tada, kai jautiesi saugus, o kitu metu lieki nuošalyje.',
        'Toks saikingumas saugo tave nuo kritikos ir nusivylimų – tai išmintinga. Tiesiog atsimink, kad pasaulis nusipelno pamatyti ir tavo talentus: kuo dažniau leisi sau spindėti, tuo daugiau džiaugsmo patirsi pats.\nKviečiame pasinaudoti kitais portalo savęs pažinimo įrankiais, kurie padės drąsiau atskleisti tavo kūrybinę prigimtį.',
      ],
    },
    tie: {
      title: 'Tavo energija: Potencialo Kryžkelė (Maksimalus svyravimas)',
      color: 'blue',
      paragraphs: [
        'vienu metu savyje jauti ir norą laisvai kurti bei spindėti (Pliusas), ir polinkį užgniaužti saviraišką dėl baimės (Minusas), ir poreikį reikštis tik atsargiai (Lankstumas).',
        'Tai įdomus pereinamasis taškas – tavyje vienu metu gyvena kelios skirtingos kryptys. Tai natūralu ir laikina: kai atrasi, kas tau iš tiesų teikia džiaugsmą, tavo kūrybinė energija susitelks ir suspindės. Ši diagnostika – švelnus kvietimas stabtelėti ir įsiklausyti į save.\nKviečiame užsiregistruoti, išsaugoti šiuos duomenis ir pasinaudoti portalo įrankiais, kurie padės sukalibruoti tavo vidinį kompasą.',
      ],
    },
  },

  // ═══════════════════════════════════════════════════════════
  // BASE NUMBER 4 — Stability, order, discipline, foundations
  // ═══════════════════════════════════════════════════════════
  4: {
    a: {
      title: 'Tavo energija: Tvirtas pamatas (Pliuso būsena)',
      color: 'emerald',
      paragraphs: [
        'tu moki kurti tvarką, būti patikimas ir kantriai statyti tvirtą gyvenimo pagrindą. Esi ta atrama, ant kurios remiasi kiti, ir gebi pabaigti tai, ką pradedi.',
        'Tai tvirta ir įkvepianti būsena – tavo nuoseklumas ir patikimumas kuria saugumą tau ir aplinkiniams. Didžiuokis tuo, ką pastatei, ir leisk sau tuo pasimėgauti. O kai panorėsi šį tvirtumą papildyti dar didesniu lengvumu, portale rasi kitų savęs pažinimo įrankių, kurie švelniai palydės tave toliau.',
      ],
    },
    b: {
      title: 'Tavo energija: Sustabarėjęs saugumas (Minuso būsena)',
      color: 'amber',
      paragraphs: [
        'šiuo metu stipriau įsikibai į tai, kas saugu ir įprasta, o pokyčiai ar netikėtumai kelia nerimą.',
        'Svarbu atsiminti – tai tik tavo dabartinė būsena, o ne tavo prigimtis. Galbūt šiandien tiesiog reikia daugiau saugumo ir tvirto pagrindo, ir tai visiškai normalu. Energija nestovi vietoje: jau rytoj tavo pasirinkimai gali pakrypti kita linkme. Pabandyk įvesti kitą datą arba pasinaudok kitais portalo savęs pažinimo įrankiais – jie švelniai padės atsipalaiduoti ir vėl pasitikėti gyvenimo tėkme.',
      ],
    },
    c: {
      title: 'Tavo energija: Lankstus stabilumas (Per vidurį)',
      color: 'purple',
      paragraphs: [
        'tu balansuoji tarp tvarkos ir laisvės – planuoji, bet leidi sau koreguoti kelią pagal aplinkybes.',
        'Toks lankstumas saugo tave tiek nuo sustabarėjimo, tiek nuo chaoso – tai sveikas vidurio kelias. Tiesiog atsimink, kad kai kuriuos pamatinius dalykus verta užbaigti iki galo: tvirtas pagrindas suteikia daugiau laisvės, nei atrodo.\nKviečiame pasinaudoti kitais portalo savęs pažinimo įrankiais, kurie padės rasti tavo pusiausvyrą tarp disciplinos ir laisvės.',
      ],
    },
    tie: {
      title: 'Tavo energija: Potencialo Kryžkelė (Maksimalus svyravimas)',
      color: 'blue',
      paragraphs: [
        'vienu metu savyje jauti ir gebėjimą kurti tvirtą tvarką (Pliusas), ir polinkį įstrigti sustabarėjusiame saugume (Minusas), ir norą balansuoti tarp struktūros bei laisvės (Lankstumas).',
        'Tai įdomus pereinamasis taškas – tavyje vienu metu gyvena kelios skirtingos kryptys. Tai natūralu ir laikina: kai suprasi, koks stabilumas tave iš tiesų stiprina, jausies tvirčiau ir laisviau. Ši diagnostika – švelnus kvietimas stabtelėti ir įsiklausyti į save.\nKviečiame užsiregistruoti, išsaugoti šiuos duomenis ir pasinaudoti portalo įrankiais, kurie padės sukalibruoti tavo vidinį kompasą.',
      ],
    },
  },

  // ═══════════════════════════════════════════════════════════
  // BASE NUMBER 5 — Freedom, change, adaptability, adventure
  // ═══════════════════════════════════════════════════════════
  5: {
    a: {
      title: 'Tavo energija: Laisvas judėjimas (Pliuso būsena)',
      color: 'emerald',
      paragraphs: [
        'tu su smalsumu priimi pokyčius ir moki prisitaikyti, o laisvę naudoji ne pabėgimui, o atsakingam kūrimui.',
        'Tai gaivinanti, dinamiška būsena – tavo lankstumas ir drąsa atveria naujas duris tau ir įkvepia kitus. Mėgaukis šia laisve ir leisk jai vesti tave į nuotykius. O kai panorėsi šią energiją nukreipti dar tikslingiau, portale rasi kitų savęs pažinimo įrankių, kurie švelniai palydės tave toliau.',
      ],
    },
    b: {
      title: 'Tavo energija: Blaškymasis ir bėgimas (Minuso būsena)',
      color: 'amber',
      paragraphs: [
        'šiuo metu laisvė tarsi virsta blaškymusi – ieškai vis naujų įspūdžių ar bėgi nuo to, kas nepatogu, užuot su tuo susidūręs.',
        'Svarbu atsiminti – tai tik tavo dabartinė būsena, o ne tavo esmė. Galbūt šiandien tiesiog reikia daugiau judėjimo ir įspūdžių, ir tai natūralu. Energija nestovi vietoje: jau rytoj tavo pasirinkimai gali pakrypti kita linkme. Pabandyk įvesti kitą datą arba pasinaudok kitais portalo savęs pažinimo įrankiais – jie švelniai padės laisvę paversti tikra gyvenimo pilnatve.',
      ],
    },
    c: {
      title: 'Tavo energija: Apgalvota laisvė (Per vidurį)',
      color: 'purple',
      paragraphs: [
        'tu balansuoji tarp nuotykio ir saugumo – mėgsti naujoves, bet stengiesi išlaikyti bent minimalią tvarką ir saiką.',
        'Toks lankstumas saugo tave tiek nuo sustabarėjimo, tiek nuo chaoso – tai išmintinga pusiausvyra. Tiesiog atsimink, kad kartais verta leisti savo dinamiškai prigimčiai ir laisvai pasireikšti: drąsesnis žingsnis gali atnešti daug gyvybės.\nKviečiame pasinaudoti kitais portalo savęs pažinimo įrankiais, kurie padės derinti drąsą keistis su gebėjimu užbaigti pradėtus dalykus.',
      ],
    },
    tie: {
      title: 'Tavo energija: Potencialo Kryžkelė (Maksimalus svyravimas)',
      color: 'blue',
      paragraphs: [
        'vienu metu savyje jauti ir norą laisvai judėti bei keistis (Pliusas), ir polinkį blaškytis ar bėgti (Minusas), ir poreikį atsargiai laviruoti (Lankstumas).',
        'Tai įdomus pereinamasis taškas – tavyje vienu metu gyvena kelios skirtingos kryptys. Tai natūralu ir laikina: kai suprasi, kokia laisvė tave iš tiesų stiprina, tavo dinamiška energija susitelks ir ves pirmyn. Ši diagnostika – švelnus kvietimas stabtelėti ir įsiklausyti į save.\nKviečiame užsiregistruoti, išsaugoti šiuos duomenis ir pasinaudoti portalo įrankiais, kurie padės sukalibruoti tavo vidinį kompasą.',
      ],
    },
  },

  // ═══════════════════════════════════════════════════════════
  // BASE NUMBER 6 — Love, responsibility, care, family, harmony
  // ═══════════════════════════════════════════════════════════
  6: {
    a: {
      title: 'Tavo energija: Brandi meilė (Pliuso būsena)',
      color: 'emerald',
      paragraphs: [
        'tu rūpiniesi kitais nuoširdžiai, bet neprarasdamas savęs, ir kuri aplink save darną.',
        'Tai šilta ir įkvepianti būsena – tavo meilė ir rūpestis kuria namų jausmą visiems aplinkui. Džiaukis šiuo gebėjimu mylėti ir globoti. O kai panorėsi šią šilumą papildyti dar didesniu rūpesčiu savimi, portale rasi kitų savęs pažinimo įrankių, kurie švelniai palydės tave toliau.',
      ],
    },
    b: {
      title: 'Tavo energija: Aukojimasis ir kontrolė (Minuso būsena)',
      color: 'amber',
      paragraphs: [
        'šiuo metu rūpinimasis kitais tarsi virsta savęs aukojimu – pamiršti save, perimi svetimą atsakomybę arba globodamas bandai kontroliuoti.',
        'Svarbu atsiminti – tai tik tavo dabartinė būsena, o ne tavo esmė. Galbūt šiandien tiesiog atiduodi daugiau, nei gauni, ir tai suprantama. Energija nestovi vietoje: jau rytoj tavo pasirinkimai gali pakrypti kita linkme. Pabandyk įvesti kitą datą arba pasinaudok kitais portalo savęs pažinimo įrankiais – jie švelniai padės mylėti kitus neprarandant savęs.',
      ],
    },
    c: {
      title: 'Tavo energija: Atsargi globa (Per vidurį)',
      color: 'purple',
      paragraphs: [
        'tu rūpiniesi kitais saikingai – padedi, kai turi jėgų, bet vengi pernelyg įsitraukti.',
        'Toks saikingumas saugo tave nuo išsekimo – tai sveika ir išmintinga. Tiesiog atsimink, kad atvira širdis dovanoja gilų artumą: kuo drąsiau dalinsies savo šiluma, tuo daugiau bendrumo džiaugsmo patirsi pats.\nKviečiame pasinaudoti kitais portalo savęs pažinimo įrankiais, kurie padės rasti pusiausvyrą tarp atviros meilės ir sveikų ribų.',
      ],
    },
    tie: {
      title: 'Tavo energija: Potencialo Kryžkelė (Maksimalus svyravimas)',
      color: 'blue',
      paragraphs: [
        'vienu metu savyje jauti ir gebėjimą brandžiai mylėti (Pliusas), ir polinkį aukotis bei kontroliuoti (Minusas), ir norą rūpintis tik atsargiai (Lankstumas).',
        'Tai įdomus pereinamasis taškas – tavyje vienu metu gyvena kelios skirtingos kryptys. Tai natūralu ir laikina: kai aiškiau atskirsi savo poreikius nuo kitų lūkesčių, tavo meilė taps dar darnesnė ir lengvesnė. Ši diagnostika – švelnus kvietimas stabtelėti ir įsiklausyti į save.\nKviečiame užsiregistruoti, išsaugoti šiuos duomenis ir pasinaudoti portalo įrankiais, kurie padės sukalibruoti tavo vidinį kompasą.',
      ],
    },
  },

  // ═══════════════════════════════════════════════════════════
  // BASE NUMBER 7 — Wisdom, introspection, spirituality, analysis
  // a) Plus: deep insight, trusts intuition, seeks meaning, wise solitude
  // b) Minus: isolation, overthinking, cynicism, emotional withdrawal
  // c) Center: cautious seeking, balances depth and everyday life
  // tie: crossroads, maximum fluctuation
  // ═══════════════════════════════════════════════════════════
  7: {
    a: {
      title: 'Tavo energija: Gilus įžvalgumas (Pliuso būsena)',
      color: 'emerald',
      paragraphs: [
        'tu moki įsiklausyti į tylą ir savo vidinį žinojimą, ieškai gilesnės prasmės ir matai tai, ko kiti nepastebi.',
        'Tai rami ir įkvepianti būsena – tavo išmintis, intuicija ir gebėjimas mąstyti giliai dovanoja aiškumą tau ir aplinkiniams. Mėgaukis šiuo vidiniu turtingumu ir pasitikėk savo žinojimu. O kai panorėsi šią gelmę dar labiau praplėsti, portale rasi kitų savęs pažinimo įrankių, kurie švelniai palydės tave toliau.',
      ],
    },
    b: {
      title: 'Tavo energija: Užsisklendimas (Minuso būsena)',
      color: 'amber',
      paragraphs: [
        'šiuo metu labiau linksti atsiriboti, pasinerti į mintis ar abejones ir laikyti pasaulį saugiu atstumu.',
        'Svarbu atsiminti – tai tik tavo dabartinė būsena, o ne tavo esmė. Galbūt šiandien tiesiog reikia daugiau tylos ir vienatvės, ir tai visiškai natūralu. Energija nestovi vietoje: jau rytoj tavo pasirinkimai gali pakrypti kita linkme. Pabandyk įvesti kitą datą arba pasinaudok kitais portalo savęs pažinimo įrankiais – jie švelniai padės vėl atsiverti pasauliui ir pasitikėti savo vidiniu žinojimu.',
      ],
    },
    c: {
      title: 'Tavo energija: Apgalvotas ieškojimas (Per vidurį)',
      color: 'purple',
      paragraphs: [
        'tu balansuoji tarp gelmės ir kasdienybės – mąstai ir giliniesi, bet stengiesi nepasiklysti vien apmąstymuose.',
        'Toks saikingumas saugo tave tiek nuo paviršutiniškumo, tiek nuo užsisklendimo savyje – tai išmintinga pusiausvyra. Tiesiog atsimink, kad tavo gilus žinojimas nusipelno būti išgirstas: kuo labiau juo pasitikėsi, tuo aiškesnis taps tavo kelias.\nKviečiame pasinaudoti kitais portalo savęs pažinimo įrankiais, kurie padės sujungti tavo intuiciją su kasdieniu gyvenimu.',
      ],
    },
    tie: {
      title: 'Tavo energija: Potencialo Kryžkelė (Maksimalus svyravimas)',
      color: 'blue',
      paragraphs: [
        'vienu metu savyje jauti ir gilų įžvalgumą bei intuiciją (Pliusas), ir polinkį užsisklęsti ar abejoti (Minusas), ir norą ieškoti atsargiai (Lankstumas).',
        'Tai įdomus pereinamasis taškas – tavyje vienu metu gyvena kelios skirtingos kryptys. Tai natūralu ir laikina: kai labiau pasitikėsi savo vidiniu žinojimu, tavo mintys nustos blaškytis ir taps aiškia įžvalga. Ši diagnostika – švelnus kvietimas stabtelėti ir įsiklausyti į save.\nKviečiame užsiregistruoti, išsaugoti šiuos duomenis ir pasinaudoti portalo įrankiais, kurie padės sukalibruoti tavo vidinį kompasą.',
      ],
    },
  },

  // ═══════════════════════════════════════════════════════════
  // BASE NUMBER 8 — Power, material success, money, ambition
  // a) Plus: healthy ambition, balanced power, abundance, effective leadership
  // b) Minus: control/domination, workaholism, money anxiety, fear of power
  // c) Center: cautious ambition, moderate drive, balanced success
  // tie: crossroads, maximum fluctuation
  // ═══════════════════════════════════════════════════════════
  8: {
    a: {
      title: 'Tavo energija: Galios meistrystė (Pliuso būsena)',
      color: 'emerald',
      paragraphs: [
        'tu valdai savo ambicijas ir galią užtikrintai bei atsakingai, matai galimybes ten, kur kiti mato kliūtis, ir gebi paversti idėjas apčiuopiamais rezultatais.',
        'Tai galinga ir įkvepianti būsena – tavo ryžtas, organizuotumas ir vidinis autoritetas atveria duris tau ir įkvepia aplinkinius siekti daugiau. Tu nebijai atsakomybės ir moki priimti sprendimus net tada, kai statoma daug; būtent dėl to žmonės tavimi pasitiki ir noriai seka paskui tave. Tavo santykis su materialiu pasauliu yra brandus – pinigus ir įtaką matai ne kaip tikslą savaime, o kaip įrankį kurti vertę sau ir kitiems. Mėgaukis šiuo gebėjimu kurti gausą ir drąsiai vesti pirmyn. O kai panorėsi šią galią praplėsti dar didesne vidine pusiausvyra, portale rasi kitų savęs pažinimo įrankių, kurie švelniai palydės tave toliau.',
      ],
    },
    b: {
      title: 'Tavo energija: Kontrolė ir spaudimas (Minuso būsena)',
      color: 'amber',
      paragraphs: [
        'šiuo metu galia ir ambicijos tarsi virsta įtampa – arba per daug spaudi save ir kitus siekdamas kontrolės, arba slopini savo užmojus dėl baimės suklysti ar pralaimėti.',
        'Svarbu atsiminti – tai tik tavo dabartinė būsena, o ne tavo esmė. Galbūt šiandien tiesiog jauti didesnį spaudimą ar nerimą dėl rezultatų, pinigų ar to, kaip tave vertina kiti, ir tai visiškai suprantama. Kartais didžiausia jėga slypi ne kontrolėje, o gebėjime atsipalaiduoti ir pasitikėti procesu. Tikra galia neišsenka – ji tiesiog laukia, kol vėl atrasi savo vidinę atramą. Energija nestovi vietoje: jau rytoj tavo pasirinkimai gali pakrypti kita linkme. Pabandyk įvesti kitą datą arba pasinaudok kitais portalo savęs pažinimo įrankiais – jie švelniai padės atrasti pusiausvyrą tarp galios ir vidinės ramybės.',
      ],
    },
    c: {
      title: 'Tavo energija: Apgalvota ambicija (Per vidurį)',
      color: 'purple',
      paragraphs: [
        'tu balansuoji tarp užmojo ir saugumo – sieki rezultatų, bet judi atsargiai, kad pernelyg nerizikuotum ir neperspaustum savęs.',
        'Toks saikingumas saugo tave nuo perdegimo ir neapgalvotos rizikos – tai sveika ir išmintinga laikysena, kuri daugeliui ateina tik su patirtimi. Tu gebi pasverti galimybes blaiviai, neapakintas nei godumo, nei baimės, ir todėl retai imiesi to, ko negali suvaldyti. Tiesiog atsimink, kad tavyje glūdi tikra kūrybinė ir vadovaujanti galia: kuo drąsiau ja pasitikėsi, tuo daugiau durų prieš tave atsivers. Kartais vienas ryžtingesnis žingsnis gali atnešti daugiau nei dešimt atsargių.\nKviečiame pasinaudoti kitais portalo savęs pažinimo įrankiais, kurie padės derinti ambiciją su vidine pusiausvyra.',
      ],
    },
    tie: {
      title: 'Tavo energija: Potencialo Kryžkelė (Maksimalus svyravimas)',
      color: 'blue',
      paragraphs: [
        'vienu metu savyje jauti ir gebėjimą tvirtai valdyti galią bei kurti rezultatus (Pliusas), ir polinkį pernelyg kontroliuoti ar slopinti save (Minusas), ir norą siekti tik atsargiai (Lankstumas).',
        'Tai įdomus pereinamasis taškas – tavyje vienu metu gyvena kelios skirtingos kryptys, ir kiekviena tarsi traukia į savo pusę. Tai natūralu ir laikina: kai suprasi, kokia galia tave iš tiesų stiprina, o ne sekina, tavo energija nustos blaškytis ir susitelks į kūrybą. Tikras vidinis autoritetas gimsta ne iš spaudimo, o iš aiškumo, kas tau iš tiesų svarbu. Ši diagnostika – švelnus kvietimas stabtelėti ir įsiklausyti į save.\nKviečiame užsiregistruoti, išsaugoti šiuos duomenis ir pasinaudoti portalo įrankiais, kurie padės sukalibruoti tavo vidinį kompasą.',
      ],
    },
  },

  // ═══════════════════════════════════════════════════════════
  // BASE NUMBER 9 — Compassion, completion, wisdom, universal love
  // a) Plus: compassionate, wise, generous, lets go gracefully
  // b) Minus: overwhelmed by others' pain, cannot let go, disillusioned
  // c) Center: cautious compassion, balanced giving, grounded idealism
  // tie: crossroads, maximum fluctuation
  // ═══════════════════════════════════════════════════════════
  9: {
    a: {
      title: 'Tavo energija: Plati širdis (Pliuso būsena)',
      color: 'emerald',
      paragraphs: [
        'tu žvelgi į pasaulį plačiai ir su gilia atjauta, moki atleisti, paleisti ir mylėti nereikalaudamas nieko mainais.',
        'Tai išmintinga ir įkvepianti būsena – tavo užuojauta, dosnumas ir gebėjimas matyti didesnį paveikslą dovanoja viltį ir paguodą visiems aplinkui. Tu supranti, kad pabaigos yra natūrali gyvenimo dalis, ir moki paleisti tai, kas jau atliko savo vaidmenį. Tavo brandus jautrumas leidžia tau giliai jausti kitus, neprarandant savo vidinės atramos. Mėgaukis šia vidine pilnatve ir leisk savo šviesai sklisti toliau. O kai panorėsi šią gelmę praplėsti dar labiau, portale rasi kitų savęs pažinimo įrankių, kurie švelniai palydės tave toliau.',
      ],
    },
    b: {
      title: 'Tavo energija: Nešamas svoris (Minuso būsena)',
      color: 'amber',
      paragraphs: [
        'šiuo metu plati tavo širdis tarsi neša per didelį svorį – arba persiimi viso pasaulio skausmu, arba negali paleisti to, kas jau pasibaigė.',
        'Svarbu atsiminti – tai tik tavo dabartinė būsena, o ne tavo esmė. Galbūt šiandien tiesiog jauti daugiau pasaulio ir savo paties skausmo, nei pajėgi panešti, ir tai visiškai suprantama. Net ir didžiausia užuojauta prasideda nuo švelnumo sau pačiam – negali pripildyti kitų iš tuščios taurės. Energija nestovi vietoje: jau rytoj tavo pasirinkimai gali pakrypti kita linkme. Pabandyk įvesti kitą datą arba pasinaudok kitais portalo savęs pažinimo įrankiais – jie švelniai padės paleisti per didelę naštą ir vėl atrasti vidinę ramybę.',
      ],
    },
    c: {
      title: 'Tavo energija: Saikingas dosnumas (Per vidurį)',
      color: 'purple',
      paragraphs: [
        'tu rūpiniesi pasauliu ir kitais, bet stengiesi neužsikrauti viso jo skausmo ir pirmiausia tvarkaisi savo kasdienybę.',
        'Toks saikingumas saugo tave nuo išsekimo ir nusivylimo – tai brandi ir sveika laikysena. Tu gebi būti geras kitiems, neprarasdamas savo paties pagrindo po kojomis, ir žinai, kad pagalba būna tikra tik tada, kai ji neišsekina tavęs. Tiesiog atsimink, kad tavyje glūdi tikra plati širdis: kai jausiesi saugus, gali drąsiau atverti ją pasauliui ir patirti gilų bendrumo džiaugsmą.\nKviečiame pasinaudoti kitais portalo savęs pažinimo įrankiais, kurie padės derinti dosnumą su rūpesčiu savimi.',
      ],
    },
    tie: {
      title: 'Tavo energija: Potencialo Kryžkelė (Maksimalus svyravimas)',
      color: 'blue',
      paragraphs: [
        'vienu metu savyje jauti ir plačią, atjautingą širdį (Pliusas), ir polinkį neštis per didelį svorį ar neatleisti (Minusas), ir norą duoti tik atsargiai (Lankstumas).',
        'Tai įdomus pereinamasis taškas – tavyje vienu metu gyvena kelios skirtingos kryptys. Tai natūralu ir laikina: kai išmoksi dosniai duoti pasauliui, nepamiršdamas savęs, tavo plati širdis taps ne našta, o tikra stiprybe. Ši diagnostika – švelnus kvietimas stabtelėti ir įsiklausyti į save.\nKviečiame užsiregistruoti, išsaugoti šiuos duomenis ir pasinaudoti portalo įrankiais, kurie padės sukalibruoti tavo vidinį kompasą.',
      ],
    },
  },
};

/**
 * Get a single interpretation block for a base number + result type.
 * @param {number} baseNumber
 * @param {'a'|'b'|'c'|'tie'} resultType
 * @returns {{title:string,color:string,paragraphs:string[]}|null}
 */
export function getInterpretation(baseNumber, resultType) {
  return DIAGNOSTIC_INTERPRETATIONS[baseNumber]?.[resultType] || null;
}
